import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import styles from './clientGroups.module.scss';
import { config } from '../../api/config';
import { actions } from '../../state';
import {
  generateHeader,
  isFilterActive,
  mapUserStatus,
  mapUserStatusColor,
  buildTableFilterBody,
} from '../../utils';
import {
  Button,
  ButtonSimple,
  Header,
  RangeFilter,
  SelectFilter,
  Table,
  TableRow,
  TableCell,
  Title,
} from '../../components';

const PAGE_SIZE = config.DEFAULTS.recordsPerPage;

const ClientGroups = ({
  dispatch,
  history,
  clientGroups: { groups, total, loading, header },
}) => {
  const [page, setPage] = useState(1);
  const pageCount = Math.ceil(total / PAGE_SIZE) || 1;
  const [nameFilter, setNameFilter] = useState([]);
  const [clientCountGt, setClientCountGt] = useState('');
  const [clientCountLt, setClientCountLt] = useState('');
  const [statusFilter, setStatusFilter] = useState([]);
  const [sortOrder, setSortOrder] = useState('asc');
  const [filterBody, setFilterBody] = useState({});

  const nameOptions = useMemo(() => generateHeader(header?.name || []), [
    header,
  ]);

  const fetchGroups = useCallback(
    params => {
      dispatch(actions.clientGroups.getClientGroupsAction(params));
    },
    [dispatch]
  );

  const clearGroups = useCallback(() => {
    dispatch(actions.clientGroups.clearClientGroupsAction());
  }, [dispatch]);

  useEffect(() => {
    if (page > pageCount) {
      setPage(1);
    }
  }, [page, pageCount]);

  useEffect(() => {
    fetchGroups({ page, pageSize: PAGE_SIZE, ...filterBody });
  }, [fetchGroups, filterBody, page]);

  useEffect(() => () => clearGroups(), [clearGroups]);

  useEffect(() => {
    dispatch(actions.clients.setExportIdentifierAction({}));
  }, []);

  const handleRowClick = id => {
    history.push(config.APP_PATHS.clientGroupDetails(id));
  };

  const handleEditClick = (event, id) => {
    event.stopPropagation();
    history.push(config.APP_PATHS.clientGroupsEdit(id));
  };
  const handleStatusToggleClick = (event, id, status) => {
    event.stopPropagation();
    const nextStatus = status === 1 ? 0 : 1;
    dispatch(
      actions.clientGroups.deactivateClientGroupAction(
        id,
        {
          page,
          pageSize: PAGE_SIZE,
          ...filterBody,
        },
        nextStatus
      )
    );
  };
  const handleSyncClick = (event, id) => {
    event.stopPropagation();
    dispatch(
      actions.clientGroups.syncClientGroupAction(id, {
        page,
        pageSize: PAGE_SIZE,
        ...filterBody,
      })
    );
  };
  const handleUsersClick = (event, id) => {
    event.stopPropagation();
    const params = new URLSearchParams({ client_group_id: String(id) });
    history.push(`${config.APP_PATHS.clients}?${params.toString()}`);
  };

  const handleFilterSubmit = submitOptions => {
    const generatedFilter = [];

    if (nameFilter.length) {
      generatedFilter.push({ field: 'name', value: nameFilter });
    }

    if (String(clientCountGt).trim() !== '') {
      generatedFilter.push({
        field: 'client_count',
        expression: 'gte',
        value: Number(clientCountGt),
        type: 'number',
      });
    }

    if (String(clientCountLt).trim() !== '') {
      generatedFilter.push({
        field: 'client_count',
        expression: 'lte',
        value: Number(clientCountLt),
        type: 'number',
      });
    }
    if (statusFilter.length) {
      generatedFilter.push({ field: 'status', value: statusFilter });
    }

    setFilterBody(
      buildTableFilterBody({
        submitOptions,
        generatedFilter,
        sortOrder,
      }) || {}
    );
    setPage(1);
  };

  const clearFilters = () => {
    setNameFilter([]);
    setClientCountGt('');
    setClientCountLt('');
    setStatusFilter([]);
    setFilterBody({});
    setPage(1);
  };

  const hasActiveFilters =
    !!nameFilter.length ||
    !!statusFilter.length ||
    String(clientCountGt).trim() !== '' ||
    String(clientCountLt).trim() !== '' ||
    !!filterBody.filter;

  const tableHeader = [
    <SelectFilter
      title="Pavadinimas"
      name="name"
      array={nameOptions}
      valueType="string"
      filterState={nameFilter}
      setFilterState={setNameFilter}
      onSubmit={handleFilterSubmit}
      sortOrder={sortOrder}
      setSortOrder={setSortOrder}
      isActive={isFilterActive('select', nameFilter)}
    />,
    <RangeFilter
      title="Vartotojų sk."
      name="client_count"
      gt={clientCountGt}
      setGt={setClientCountGt}
      lt={clientCountLt}
      setLt={setClientCountLt}
      onSubmit={handleFilterSubmit}
      sortOrder={sortOrder}
      setSortOrder={setSortOrder}
      isActive={isFilterActive('range', clientCountLt, clientCountGt)}
      position="right"
      customWidth="100%"
    />,
    <SelectFilter
      title="Statusas"
      name="status"
      valueType="number"
      array={generateHeader(header?.status || [], mapUserStatus)}
      filterState={statusFilter}
      setFilterState={setStatusFilter}
      onSubmit={handleFilterSubmit}
      sortOrder={sortOrder}
      setSortOrder={setSortOrder}
      isActive={isFilterActive('select', statusFilter)}
    />,
    '',
  ];

  const renderTable = () =>
    groups.map(({ id, name, usersCount, status }) => {
      const isActive = status === 1;
      return (
        <TableRow
          onClick={() => handleRowClick(id)}
          key={id}
          disabled={!isActive}
        >
          <TableCell>{name}</TableCell>
          <TableCell>
            <button
              type="button"
              className={styles.cellUsersButton}
              onClick={event => handleUsersClick(event, id)}
            >
              {usersCount}
            </button>
          </TableCell>
          <TableCell>
            <span style={{ color: mapUserStatusColor(status) }}>
              {mapUserStatus(status)}
            </span>
          </TableCell>
          <TableCell align="right">
            <div className={styles.actions}>
              <Button
                color="outline"
                onClick={event => handleStatusToggleClick(event, id, status)}
                classes={{ button: styles.rowButton }}
              >
                {isActive ? 'Deaktyvuoti' : 'Aktyvuoti'}
              </Button>
              <Button
                color="outline"
                onClick={event => handleSyncClick(event, id)}
                classes={{ button: styles.rowButton }}
                disabled={!isActive}
              >
                Sinchronizuoti
              </Button>
              <Button
                color="outline"
                onClick={event => handleEditClick(event, id)}
                classes={{ button: styles.rowButton }}
              >
                Redaguoti
              </Button>
            </div>
          </TableCell>
        </TableRow>
      );
    });

  return (
    <section className={styles.page}>
      <Header size="lg">
        <Title>Klientų grupės</Title>
        <div className={styles.headerActions}>
          {hasActiveFilters && (
            <ButtonSimple type="filter" onClick={clearFilters}>
              Išvalyti filtrus
            </ButtonSimple>
          )}
          <Button
            color="primary"
            url={config.APP_PATHS.clientGroupsAdd}
            classes={{ button: styles.primaryButton }}
          >
            Nauja grupė
          </Button>
        </div>
      </Header>

      <Table
        header={tableHeader}
        isEmpty={!groups.length}
        filter={true}
        loading={loading}
        width="lg"
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        serverPagination={true}
        onPageChange={setPage}
      >
        {renderTable()}
      </Table>
    </section>
  );
};

const mapStateToProps = state => ({
  clientGroups: state.clientGroups,
});

ClientGroups.propTypes = {
  dispatch: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  clientGroups: PropTypes.shape({
    groups: PropTypes.array.isRequired,
    total: PropTypes.number.isRequired,
    loading: PropTypes.bool.isRequired,
    header: PropTypes.object,
  }).isRequired,
};

export default connect(mapStateToProps)(ClientGroups);
